"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const IContainerInjection_1 = require("auto-widget-framework/src/Container/IContainerInjection");
const configs_1 = require("../configs/configs");
const ErrorMessageType_1 = require("../Model/ErrorMessageType");
const WidgetError_1 = require("auto-widget-framework/src/Error/WidgetError");
const FieldValidationTypes_1 = require("auto-widget-framework/src/Enum/FieldValidationTypes");
const Mixin_1 = require("auto-widget-framework/src/Util/Mixin");
class QuestionnaireFormValidator {
    constructor(formElement, questionnaires, domHelper) {
        this.errorMessages = [];
        this.QUESTIONNAIRE_ATTRIBUTE = 'data-px-qstr-id';
        this.formElement = formElement;
        this.questionnaires = questionnaires;
        this.domHelper = domHelper;
        this.registerQuestionnaireErrorMessages();
    }
    static create(formElement, validationConfig, container) {
        return new this(formElement, validationConfig, container.get("dom_helper"));
    }
    /**
     * Registers error messages for questionnaire fields from template.
     */
    registerQuestionnaireErrorMessages() {
        const questionnaireContainer = this.formElement.querySelector(`[${this.QUESTIONNAIRE_ATTRIBUTE}="${this.questionnaires[0].getMachineName()}"]`);
        if (!questionnaireContainer) {
            throw new WidgetError_1.default(`Missing questionnaire container for: ${this.questionnaires[0].getMachineName()}.`);
        }
        const messagesContainers = questionnaireContainer.querySelectorAll(`[${configs_1.configs.elementIDs.QUESTIONNAIRE_QUESTION_ERROR_MESSAGES}]`);
        if (!messagesContainers.length) {
            console.warn(`Questionnaire '${this.questionnaires[0].getMachineName()}' is missing validation messages in form element.`);
            return;
        }
        for (let j = 0, messageContainer; messageContainer = messagesContainers[j++];) {
            for (let i = 0, message; message = messageContainer.children[i++];) {
                const errorMessageType = new ErrorMessageType_1.default();
                errorMessageType.setValidationType(message.getAttribute(configs_1.configs.elementIDs.QUESTIONNAIRE_QUESTION_ERROR_TYPE));
                errorMessageType.setFieldName(message.getAttribute(configs_1.configs.elementIDs.QUESTIONNAIRE_QUESTION_ERROR_FIELD_NAME));
                errorMessageType.setFieldType(message.getAttribute(configs_1.configs.elementIDs.QUESTIONNAIRE_QUESTION_ERROR_FIELD_TYPE));
                errorMessageType.setMessage(message.innerHTML);
                if (!Object.values(FieldValidationTypes_1.FieldValidationTypes).includes(errorMessageType.getValidationType())) {
                    throw new WidgetError_1.default(`Missing or incorrect questionnaire error message type for: ${message.innerHTML}.`);
                }
                this.errorMessages.push(errorMessageType);
            }
        }
    }
    /**
     * @inheritDoc
     */
    removeForAll() {
        return Promise.resolve().then(() => {
            const promises = this.questionnaires.map((qstr) => {
                qstr.getQuestions().map((question) => {
                    return this.removeForField(question.getId());
                });
            });
            return Promise.all(promises);
        });
    }
    /**
     * @inheritDoc
     */
    removeForField(inputName) {
        return Promise.resolve().then(() => {
            const container = this.formElement.querySelector(`[${configs_1.configs.elementIDs.QUESTIONNAIRE_QUESTION_ERRORS}="${inputName}"]`);
            if (!container) {
                return;
            }
            this.updateInputInvalidClass(inputName, false);
            container.classList.remove(configs_1.configs.utilityCSSClasses.isInvalid);
            container.innerHTML = "";
        });
    }
    /**
     * @inheritDoc
     */
    display(errors) {
        return Promise.resolve().then(() => {
            this.displayQuestionnaireErrors(errors.getValidationErrors());
        });
    }
    /**
     * @inheritDoc
     */
    displayForField(inputName, error) {
        return Promise.resolve().then(() => {
            this.displayQuestionnaireErrors({ [inputName]: error });
        });
    }
    /**
     * Displays validation errors on fields.
     */
    displayQuestionnaireErrors(errors) {
        this.questionnaires.map((qstr) => {
            qstr.getQuestions().forEach((question) => {
                const container = this.formElement.querySelector(`[${configs_1.configs.elementIDs.QUESTIONNAIRE_QUESTION_ERRORS}="${question.getId()}"]`);
                if (!container) {
                    console.warn(`Missing error container for question '${question.getId()}'.`);
                    return;
                }
                if (!errors[question.getId()]) {
                    return;
                }
                container.classList.add(configs_1.configs.utilityCSSClasses.isInvalid);
                this.updateInputInvalidClass(question.getId(), true);
                errors[question.getId()].forEach((error) => {
                    let errorMessage = this.findErrorMessageForError(error, question.getId());
                    if (errorMessage) {
                        if (container.innerHTML.indexOf(errorMessage) === -1) {
                            container.innerHTML += errorMessage
                                .replace("%min_count%", String(question.getInputMin()))
                                .replace("%max_count%", String(question.getInputMax()))
                                .replace("%field%", question.getTitle().slice(0, 1).toLocaleLowerCase() +
                                question.getTitle().slice(1));
                        }
                    }
                    else {
                        throw new WidgetError_1.default(`No questionnaire error message found for '${error.getType()}'.`);
                    }
                });
                this.showCollapsedErrors();
            });
        });
    }
    /**
     * Opens the accordion if it's closed and an error appears for one of the fields inside
     */
    showCollapsedErrors() {
        const error = this.formElement.querySelector('.px-choice-accordion .error-container.is-invalid');
        const historyAccordion = this.formElement.querySelector('.px-choice-accordion-history');
        if (error && historyAccordion && this.domHelper.isChildOf(historyAccordion, error)) {
            const accordionInput = historyAccordion.getElementsByClassName("px-collapsible")[0];
            if (accordionInput && !accordionInput.classList.contains('active')) {
                accordionInput.click();
            }
        }
    }
    /**
     * Finds error message for a specific error and field.
     *
     * @param error
     * @param fieldName
     */
    findErrorMessageForError(error, fieldName) {
        let errorMessage = null;
        let priority = 0;
        this.errorMessages.forEach((messageType) => {
            if (messageType.getValidationType() !== error.getType() ||
                (messageType.getFieldType() && messageType.getFieldType() !== error.getValueType()) ||
                (messageType.getFieldName() && messageType.getFieldName() !== fieldName)) {
                return;
            }
            // Set priority. The options that have bigger impact have bigger number.
            let newPriority = (messageType.getValidationType() ? 1 : 0) +
                (messageType.getFieldType() ? 2 : 0) +
                (messageType.getFieldName() ? 3 : 0);
            if (newPriority >= priority) {
                priority = newPriority;
                errorMessage = messageType;
            }
        });
        return errorMessage !== null ? errorMessage.getMessage() : null;
    }
    updateInputInvalidClass(id, addClass) {
        const inputContainers = this.formElement.querySelectorAll(`[${configs_1.configs.elementIDs.question}="${id}"]`);
        if (!inputContainers) {
            return;
        }
        for (let i = 0; i < inputContainers.length; i++) {
            if (addClass) {
                inputContainers[i].classList.add(configs_1.configs.utilityCSSClasses.isInvalid);
            }
            else {
                inputContainers[i].classList.remove(configs_1.configs.utilityCSSClasses.isInvalid);
            }
        }
    }
}
exports.default = QuestionnaireFormValidator;
Mixin_1.default(QuestionnaireFormValidator, IContainerInjection_1.default);
